import { Capacitor } from '@capacitor/core'

import { api } from '@/lib/api'

/**
 * Notificaciones push en el navegador.
 *
 * LA RESTRICCIÓN QUE MANDA AQUÍ ES iOS.
 *
 * Safari solo entrega Web Push si la PWA está **instalada en la pantalla de
 * inicio**. En una pestaña normal la API existe, `Notification.requestPermission`
 * responde, y aun así no llega nada: falla en silencio. Por eso esta capa
 * distingue «no se puede» de «falta instalar» — decirle a alguien que active los
 * avisos cuando su navegador nunca se los va a entregar es peor que no ofrecerlo.
 *
 * En la app nativa (Capacitor) esto no corre: ahí los avisos llegan por APNs y
 * FCM, sin service worker de por medio.
 */

export type EstadoPush =
  /** El navegador no implementa Web Push. */
  | 'no-soportado'
  /** iOS: hay soporte, pero exige instalar la app en la pantalla de inicio. */
  | 'requiere-instalacion'
  /** Se puede activar; falta pedir permiso. */
  | 'sin-permiso'
  /** El usuario dijo que no. Solo se revierte desde los ajustes del navegador. */
  | 'denegado'
  /** Permiso concedido y dispositivo dado de alta. */
  | 'activo'

const CLAVE_PUBLICA = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

/**
 * iPadOS se identifica como Mac desde iOS 13, así que el user agent no basta:
 * se añade la comprobación de pantalla táctil, que un Mac de escritorio no tiene.
 */
function esIOS(): boolean {
  const ua = navigator.userAgent
  if (/iPhone|iPod/.test(ua)) return true
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
}

/** ¿Se está ejecutando como app instalada y no como pestaña? */
export function esInstalada(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  // Safari en iOS no implementa `display-mode` y usa esta propiedad propia.
  return (window.navigator as { standalone?: boolean }).standalone === true
}

export function estadoPush(): EstadoPush {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'no-soportado'
  if (esIOS() && !esInstalada()) return 'requiere-instalacion'
  if (Notification.permission === 'denied') return 'denegado'
  if (Notification.permission === 'granted') return 'activo'
  return 'sin-permiso'
}

/**
 * Pide permiso, se suscribe y da de alta el dispositivo.
 *
 * El permiso **tiene que pedirse desde un gesto del usuario**: llamarlo al
 * cargar la página hace que el navegador lo rechace de plano, y en algunos casos
 * bloquea el dominio para futuras peticiones.
 */
export async function activarPush(): Promise<EstadoPush> {
  if (Capacitor.isNativePlatform()) return 'activo'

  const estado = estadoPush()
  if (estado === 'no-soportado' || estado === 'requiere-instalacion' || estado === 'denegado') {
    return estado
  }

  if (!CLAVE_PUBLICA) {
    throw new Error('Falta la llave pública VAPID en la configuración del sitio.')
  }

  const permiso = await Notification.requestPermission()
  if (permiso !== 'granted') return permiso === 'denied' ? 'denegado' : 'sin-permiso'

  const registro = await navigator.serviceWorker.ready

  // Si ya había suscripción se reutiliza: volver a suscribirse genera un
  // endpoint nuevo y dejaría el anterior recibiendo avisos duplicados.
  const suscripcion =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      // Obligatorio en Chrome: no admite suscripciones silenciosas.
      userVisibleOnly: true,
      applicationServerKey: base64UrlABytes(CLAVE_PUBLICA),
    }))

  const json = suscripcion.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }

  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error('La suscripción del navegador llegó incompleta.')
  }

  await api.post('/notifications/devices', {
    platform: 'WEB',
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    deviceName: nombreDispositivo(),
  })

  return 'activo'
}

export async function desactivarPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const registro = await navigator.serviceWorker.ready
  const suscripcion = await registro.pushManager.getSubscription()
  await suscripcion?.unsubscribe()
  // El alta del servidor se limpia sola: al primer 404 o 410 del servicio de
  // push, el despachador sube el contador de fallos y da de baja el registro.
}

/**
 * Nombre legible del dispositivo, para la lista de sesiones en Ajustes.
 * Se deduce del user agent, que es aproximado a propósito: no hace falta más
 * precisión para que alguien reconozca cuál de sus teléfonos es.
 */
function nombreDispositivo(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad|Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows'
  return 'Este navegador'
}

/**
 * La llave VAPID viaja en base64url y `subscribe` espera bytes crudos.
 * `atob` solo entiende base64 estándar, así que hay que revertir la variante
 * segura para URL y reponer el relleno.
 */
function base64UrlABytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const relleno = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + relleno).replace(/-/g, '+').replace(/_/g, '/')
  const binario = atob(base64)
  // Se reserva el ArrayBuffer explícitamente: `new Uint8Array(n)` se tipa como
  // `ArrayBufferLike`, que podría ser compartido, y `applicationServerKey` solo
  // admite un búfer normal.
  const bytes = new Uint8Array(new ArrayBuffer(binario.length))
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i)
  return bytes
}
